/*
 * GF-CN 1.9 (04/11/2026, Claude, theme v7.3): "Add another ornament with a different name" + "Use again" (part 1b).
 * GF-CN 1.7 (23/10/2026, Claude, theme v6.1): fill-per-line mode for ornaments (fieldset[data-gf-cn-dien], Kin KM-CA-NHAN 1.4):
 *   no field is required; _gf_cn = number of filled fields, capped at data-tran (2); the price label shows the live total;
 *   choosing Add personalization with every field empty blocks add-to-cart with a message on the first field.
 * GF-CN 1.3 (21/10/2026) - GenusFaith custom name. Port of GerberaPrints GP_CN 2.5 + GP_CNS 1.3.
 *
 * 1.5 (22/10/2026, Claude): field type 'chu' (free text, tag cn-text) for garden flags.
 * 1.6 (22/10/2026, Claude, theme v6.0): suggestion buttons [data-gf-cn-goi] fill their field (respecting maxlength);
 *   the N/max counter sits under the field and turns wine-coloured at the limit.
 *
 * Part 1, form: behaviour for every fieldset[data-gf-cn] (snippets/gf-ca-nhan.liquid). Event delegation on
 *   document, so it keeps working after Horizon morphs the product section on a variant change.
 * Part 2, cart sync: one fee line "Personalization" (variant VID, $4.95) with
 *   quantity = sum over cart lines of (line quantity x properties._gf_cn).
 *   Runs after every cart change made through fetch, XHR or the theme cart event, and once on load
 *   when layout/theme.liquid marks the cart as out of sync (script[data-gf-cn-lech]).
 * Our own cart calls carry the header X-GF-CN, so they never trigger a new sync.
 * 1.4 (21/10/2026, Claude, GF-PDP 5.4): the canvas personalization fields can sit OUTSIDE the product form (inside the
 *   GF-CV selector, snippets/gf-ca-nhan.liquid vi_tri 'cv') and join it through the form="..." attribute. Form lookups now
 *   use form.elements (which includes form-associated fields) instead of form.querySelector; a missing / wrong form
 *   attribute is repaired from the add-to-cart form of the same section.
 * 1.1 (21/10/2026): clean extra spaces on change and on the add-to-cart click (the theme reads the form
 *   before a submit listener can change it; preview test saved "  Jose   Marie ").
 * 1.3 (21/10/2026, Claude, GF-CN 1.3): variant mode. A fieldset with data-gf-cn-variant (product has an option
 *   named "Personalization") has no fee radio and no _gf_cn input; its text fields are shown, required and enabled
 *   only while the selected variant's Personalization value is not "No personalization" (read from Horizon's
 *   variant picker). No fee line is added for it (no _gf_cn property). Fieldsets without that attribute keep 1.2.
 */
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const VID = 48072270217393;
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const MARK = 'X-GF-CN';

/* ---------------------------------------------------------------- part 1: form */
const RE = {
  ten: /^[A-Za-zÀ-ÖØ-öø-ſ0-9 .,'&-]+$/,
  ngay: /^[A-Za-z0-9 .,/-]+$/,
  nam: /^[0-9]{4}$/,
  chu: /^[A-Za-z0-9 .,'&!?:;()\-]+$/,
  namkhoang: /^[0-9]{4} ?[-–] ?[0-9]{4}$/,
};
const DIEN_RONG = 'Type at least one line, or choose No personalization.';
const LOI = {
  ten: "Please use letters, numbers, spaces and . , ' & - only.",
  ngay: 'Please use letters, numbers, spaces and . , / - only.',
  nam: 'Please enter a 4-digit year, for example 2026.',
  chu: "Please use letters, numbers, spaces and . , ' & ! ? : ; ( ) - only.",
  namkhoang: 'Please enter two years, for example 1941 - 2026.',
};

function kiem(input) {
  const v = input.value.trim();
  const k = input.dataset.kieu;
  input.setCustomValidity(v && RE[k] && !RE[k].test(v) ? LOI[k] : '');
  // 1.7: fill-per-line mode - Custom chosen with every field empty blocks add-to-cart (message on the first field).
  // Lives here because gon() re-runs kiem() on every field right before the theme reads the form.
  const fs = input.closest('[data-gf-cn-dien]');
  if (fs && !input.disabled) {
    const tr = [...fs.querySelectorAll('[data-gf-cn-truong]')];
    if (tr[0] === input && tr.every((i) => !i.value.trim())) input.setCustomValidity(DIEN_RONG);
  }
}

// 1.3: variant mode - is the selected variant a personalized one?
function coBienThe(fs) {
  const ten = fs.dataset.gfCnOpt || 'Personalization';
  const vung = fs.closest('.shopify-section, dialog') || document;
  const pid = fs.dataset.pid;
  const picker =
    (pid && vung.querySelector(`variant-picker[data-product-id="${pid}"]`)) || vung.querySelector('variant-picker');
  let v = null;
  if (picker) {
    const r = Array.from(picker.querySelectorAll('fieldset input:checked')).find((i) => i.dataset.optionName === ten);
    if (r) v = r.value;
    else {
      const o = Array.from(picker.querySelectorAll('select option')).find((x) => x.dataset.optionName === ten && x.selected);
      if (o) v = o.value;
    }
  }
  if (v == null) v = fs.dataset.gfCnVal || '';
  const n = String(v).trim().toLowerCase();
  return n !== '' && n !== 'no personalization';
}

function capNhat(fs) {
  const bienThe = fs.hasAttribute('data-gf-cn-variant');
  const co = bienThe ? coBienThe(fs) : fs.querySelector('[data-gf-cn-chon]:checked')?.value === 'co';
  if (bienThe) fs.hidden = !co;
  const than = fs.querySelector('[data-gf-cn-than]');
  if (than) than.hidden = !co;
  fs.querySelectorAll('[data-gf-cn-truong],[data-gf-cn-ok],[data-gf-cn-so]').forEach((el) => {
    el.disabled = !co;
  });
  const truong = [...fs.querySelectorAll('[data-gf-cn-truong]')];
  truong.forEach((i) => {
    const dem = i.parentElement?.querySelector('[data-gf-cn-dem]');
    if (dem) {
      dem.textContent = `${i.value.length}/${i.maxLength}`;
      dem.classList.toggle('is-day', i.maxLength > 0 && i.value.length >= i.maxLength);
    }
  });
  const chu = truong.map((i) => i.value.trim()).filter(Boolean);
  if (fs.hasAttribute('data-gf-cn-dien')) {
    const gia = parseInt(fs.dataset.gia || '495', 10);
    const tran = parseInt(fs.dataset.tran || '2', 10);
    const n = Math.min(chu.length, tran);
    const so = fs.querySelector('[data-gf-cn-so]');
    if (so) so.value = String(n);
    const tong = fs.querySelector('[data-gf-cn-tong]');
    if (tong) tong.textContent = co && n ? `+$${((gia * n) / 100).toFixed(2)}` : `+$${(gia / 100).toFixed(2)} each`;
    const loi = fs.querySelector('[data-gf-cn-loi]');
    if (truong[0]) kiem(truong[0]);
    if (loi && (chu.length || !co)) loi.textContent = '';
  }
  const xem = fs.querySelector('[data-gf-cn-xem]');
  if (xem) {
    xem.textContent = chu.length ? chu.join(' · ') : xem.dataset.mau || '';
    xem.classList.toggle('is-mau', !chu.length);
  }
}

const fsCua = (t) => (t instanceof Element ? t.closest('[data-gf-cn]') : null);

// 1.4: fields of a form, including the ones outside it that join through form="..."
const truongCua = (form) =>
  Array.from(form.elements).filter((el) => el instanceof HTMLInputElement && el.matches('[data-gf-cn-truong]'));
const coCn = (form) =>
  !!(form.querySelector('[data-gf-cn]') || Array.from(form.elements).some((el) => el.closest && el.closest('[data-gf-cn]')));

// 1.4: personalization block outside the form -> make sure its fields point at the add-to-cart form of the section.
function noiForm(fs) {
  if (!fs.hasAttribute('data-gf-cn-cv')) return;
  const vung = fs.closest('.shopify-section') || document;
  let id = fs.dataset.form || '';
  if (!id || !document.getElementById(id)) {
    const f = vung.querySelector('form[data-type="add-to-cart-form"]') || vung.querySelector('form[action*="/cart/add"]');
    if (!f) return;
    if (!f.id) f.id = `GfCnForm-${Math.random().toString(36).slice(2, 8)}`;
    id = f.id;
  }
  fs.querySelectorAll('input').forEach((i) => {
    if (i.getAttribute('form') !== id) i.setAttribute('form', id);
  });
}

// 1.6: suggestion button -> fill the field of the same row.
document.addEventListener('click', (e) => {
  const b = e.target instanceof Element ? e.target.closest('[data-gf-cn-goi]') : null;
  if (!b) return;
  const i = b.closest('.gf-cn__truong')?.querySelector('[data-gf-cn-truong]');
  if (!i || i.disabled) return;
  i.value = String(b.dataset.gfCnGoi || '').slice(0, i.maxLength > 0 ? i.maxLength : undefined);
  i.dispatchEvent(new Event('input', { bubbles: true }));
  i.dispatchEvent(new Event('change', { bubbles: true }));
  i.focus();
});

document.addEventListener('input', (e) => {
  const fs = fsCua(e.target);
  if (!fs) return;
  if (e.target.matches('[data-gf-cn-truong]')) kiem(e.target);
  capNhat(fs);
});
document.addEventListener('change', (e) => {
  const fs = fsCua(e.target);
  if (fs) capNhat(fs);
  // 1.3: a variant change on Horizon's picker (also when driven by gf-cv.js) -> re-check variant-mode fieldsets.
  if (e.target instanceof Element && e.target.closest('variant-picker')) {
    document.querySelectorAll('[data-gf-cn-variant]').forEach(capNhat);
  }
});

function gon(form) {
  truongCua(form).filter((i) => !i.disabled).forEach((i) => {
    const v = i.value.trim().replace(/\s+/g, ' ');
    if (v !== i.value) i.value = v;
    kiem(i);
  });
}
document.addEventListener('change', (e) => {
  if (e.target instanceof HTMLInputElement && e.target.matches('[data-gf-cn-truong]') && e.target.form) gon(e.target.form);
});
// The theme reads the form inside its own submit handler, so clean the text on the button click (sticky bar clicks it too).
document.addEventListener(
  'click',
  (e) => {
    const b = e.target instanceof Element ? e.target.closest('button[type="submit"], button:not([type])') : null;
    if (b && b.form && coCn(b.form)) {
      keoLai(b.form);
      gon(b.form);
    }
  },
  true
);
// Last check before the theme handler (capture phase).
document.addEventListener(
  'submit',
  (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !coCn(form)) return;
    keoLai(form);
    gon(form);
    if (!form.checkValidity()) {
      form.querySelectorAll('[data-gf-cn-dien] [data-gf-cn-loi]').forEach((l) => {
        const f = l.closest('[data-gf-cn]')?.querySelector('[data-gf-cn-truong]');
        if (f && f.validationMessage) l.textContent = f.validationMessage;
      });
      e.preventDefault();
      e.stopImmediatePropagation();
      form.reportValidity();
      return;
    }
    ghiCho(form);
  },
  true
);

const lamMoiForm = () =>
  document.querySelectorAll('[data-gf-cn]').forEach((fs) => {
    noiForm(fs);
    capNhat(fs);
    hienLai(fs);
  });
window.addEventListener('pageshow', lamMoiForm);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', lamMoiForm);
else lamMoiForm();

/* ---------------------------------------------------------------- part 1b (1.9): more names + use again */
// 1.9 (04/11/2026, Claude; spec GF-CA-NHAN-GIA-DINH 1.0 approved by the founder 04/11 20:14, learned from Kin KM-CA-NHAN 1.9):
//  a) ornaments in fill-per-line mode: "Add another ornament with a different name" keeps the typed lines as one more ornament
//     of the same design and size; after the main Add to cart succeeds, every kept name is added as its own cart line
//     (quantity 1, its own properties, _gf_cn = filled lines capped at data-tran). The fee line follows through dongBo().
//  b) "Use again": the last typed lines are kept on this device only (localStorage 'gf_cn_nho_v1') and offered on the next product.
// function declarations + var only: lamMoiForm() above runs at module load, before any const/let of this part exists.
var cho = null; // { vid, ds: [{ props, n }] } waiting for the main add to finish

function docNho() {
  try {
    return JSON.parse(localStorage.getItem('gf_cn_nho_v1') || '{}') || {};
  } catch (_) {
    return {};
  }
}
function ghiNho(o) {
  try {
    localStorage.setItem('gf_cn_nho_v1', JSON.stringify(o));
  } catch (_) {
    /* private mode: ignore */
  }
}
function tenTruong(i) {
  return (i.name.match(/^properties\[(.+)\]$/) || [])[1] || '';
}
function dongDien(fs) {
  const props = {};
  fs.querySelectorAll('[data-gf-cn-truong]').forEach((i) => {
    const v = i.value.trim().replace(/\s+/g, ' ');
    if (v && !i.disabled) props[tenTruong(i)] = v;
  });
  return props;
}
function luuNho(props) {
  if (!Object.keys(props).length) return;
  ghiNho(Object.assign(docNho(), props));
}

function veDs(fs) {
  const ul = fs.querySelector('[data-gf-cn-ds]');
  if (!ul) return;
  const ds = fs.__gfDs || [];
  ul.innerHTML = '';
  ds.forEach((d, k) => {
    const li = document.createElement('li');
    li.className = 'gf-cn__ds-mon';
    const s = document.createElement('span');
    s.textContent = `Ornament ${k + 2}: ${Object.values(d.props).join(' · ')}`;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gf-cn__ds-bo';
    b.dataset.gfCnBo = String(k);
    b.setAttribute('aria-label', `Remove ornament ${k + 2}`);
    b.textContent = 'Remove';
    li.append(s, b);
    ul.appendChild(li);
  });
  ul.hidden = !ds.length;
  const nut = fs.querySelector('[data-gf-cn-them-nut]');
  if (nut) nut.textContent = ds.length ? '+ Add one more name' : '+ Add another ornament with a different name';
}

document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const nut = t.closest('[data-gf-cn-them-nut]');
  if (nut) {
    const fs = nut.closest('[data-gf-cn]');
    const loi = fs.querySelector('[data-gf-cn-loi]');
    const props = dongDien(fs);
    const ok = fs.querySelector('[data-gf-cn-ok]');
    if (!Object.keys(props).length) {
      if (loi) loi.textContent = 'Type a name first, then add the next one.';
      return;
    }
    const sai = [...fs.querySelectorAll('[data-gf-cn-truong]')].find((i) => !i.disabled && i.value.trim() && !i.checkValidity());
    if (sai) {
      if (loi) loi.textContent = sai.validationMessage;
      return;
    }
    if (ok && !ok.checked) {
      if (loi) loi.textContent = 'Please tick the spelling check for this name first.';
      ok.focus();
      return;
    }
    const tran = parseInt(fs.dataset.tran || '2', 10);
    (fs.__gfDs = fs.__gfDs || []).push({ props, n: Math.min(Object.keys(props).length, tran) });
    luuNho(props);
    fs.querySelectorAll('[data-gf-cn-truong]').forEach((i) => {
      i.value = '';
    });
    if (ok) ok.checked = false;
    if (loi) loi.textContent = '';
    veDs(fs);
    capNhat(fs);
    fs.querySelector('[data-gf-cn-truong]')?.focus();
    return;
  }
  const bo = t.closest('[data-gf-cn-bo]');
  if (bo) {
    const fs = bo.closest('[data-gf-cn]');
    (fs.__gfDs || []).splice(parseInt(bo.dataset.gfCnBo, 10), 1);
    veDs(fs);
    return;
  }
  const lai = t.closest('[data-gf-cn-lai-nut]');
  if (lai) {
    const fs = lai.closest('[data-gf-cn]');
    const nho = docNho();
    fs.querySelectorAll('[data-gf-cn-truong]').forEach((i) => {
      const v = nho[tenTruong(i)];
      if (v && !i.disabled) {
        i.value = String(v).slice(0, i.maxLength > 0 ? i.maxLength : undefined);
        i.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });
    lai.closest('[data-gf-cn-lai]').hidden = true;
  }
});

function hienLai(fs) {
  const hop = fs.querySelector('[data-gf-cn-lai]');
  if (!hop) return;
  const nho = docNho();
  const truong = [...fs.querySelectorAll('[data-gf-cn-truong]')];
  const co = truong.map((i) => nho[tenTruong(i)]).filter(Boolean);
  const trong = truong.every((i) => !i.value.trim());
  hop.hidden = !(co.length && trong);
  const sp = hop.querySelector('[data-gf-cn-lai-chu]');
  if (sp) sp.textContent = co.join(' · ');
}

// Called from the submit capture handler once the form is valid.
function ghiCho(form) {
  const fs = Array.from(form.querySelectorAll('[data-gf-cn]')).concat(
    Array.from(form.elements).map((el) => el.closest && el.closest('[data-gf-cn]')).filter(Boolean)
  )[0];
  if (!fs) return;
  const chon = fs.querySelector('[data-gf-cn-chon]:checked');
  if (chon && chon.value !== 'co') return;
  luuNho(dongDien(fs));
  const ds = fs.__gfDs || [];
  const vid = form.elements.id && form.elements.id.value;
  cho = ds.length && vid ? { vid, ds: ds.slice(), fs } : null;
}

async function chayThem() {
  const c = cho;
  cho = null;
  if (!c) return;
  try {
    const items = c.ds.map((d) => ({ id: Number(c.vid), quantity: 1, properties: Object.assign({}, d.props, { _gf_cn: String(d.n) }) }));
    const r = await goi('cart/add.js', { items });
    if (!r.ok) console.warn('[gf-cn] add names HTTP', r.status);
    c.fs.__gfDs = [];
    veDs(c.fs);
    await dongBo();
    baoTheme(await layGio());
  } catch (err) {
    console.warn('[gf-cn] add names', err);
  }
}

// Main add with every field empty but names kept: move the last kept name back into the fields (it becomes ornament 1).
function keoLai(form) {
  const fs =
    form.querySelector('[data-gf-cn-dien]') ||
    Array.from(form.elements).map((el) => el.closest && el.closest('[data-gf-cn-dien]')).find(Boolean);
  if (!fs || !(fs.__gfDs || []).length) return;
  const truong = [...fs.querySelectorAll('[data-gf-cn-truong]')];
  if (truong.some((i) => i.value.trim())) return;
  const d = fs.__gfDs.pop();
  truong.forEach((i) => {
    const v = d.props[tenTruong(i)];
    if (v) i.value = v;
  });
  const ok = fs.querySelector('[data-gf-cn-ok]');
  if (ok) ok.checked = true;
  veDs(fs);
  capNhat(fs);
}

/* ---------------------------------------------------------------- part 2: cart sync */
const _fetch = window.fetch.bind(window);
const LA_GIO = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
let hen = 0;
let dang = false;
let lai = false;
let tuPhat = false;

function henDongBo(ms = 150) {
  clearTimeout(hen);
  hen = setTimeout(dongBo, ms);
}

window.fetch = function (input, init) {
  const p = _fetch(input, init);
  try {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input && input.url) || '';
    if (LA_GIO.test(url)) {
      const h = new Headers((init && init.headers) || (input instanceof Request ? input.headers : undefined));
      if (!h.has(MARK))
        p.then((r) => {
          if (r.ok) {
            henDongBo();
            if (/\/cart\/add/.test(url)) chayThem();
          }
        }).catch(() => {});
    }
  } catch (_) {
    /* never break the caller */
  }
  return p;
};

const _open = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (method, url) {
  try {
    if (LA_GIO.test(String(url))) {
      this.addEventListener('loadend', () => {
        if (this.status >= 200 && this.status < 300) {
          henDongBo();
          if (/\/cart\/add/.test(String(url))) chayThem();
        }
      });
    }
  } catch (_) {
    /* never break the caller */
  }
  return _open.apply(this, arguments);
};

// Cart changes made through Shopify.actions / Storefront API do not hit /cart/*.js; the theme event covers them.
document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
  if (tuPhat) return;
  Promise.resolve(e.promise)
    .catch(() => {})
    .finally(() => {
      henDongBo(300);
      if (e.action === 'add' && cho) chayThem();
    });
});

const layGio = async () =>
  (
    await _fetch(ROOT + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', [MARK]: '1' },
    })
  ).json();

const goi = (path, body) =>
  _fetch(ROOT + path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', [MARK]: '1' },
    body: JSON.stringify(body),
  });

function baoTheme(cart) {
  try {
    const d = CartLinesUpdateEvent.createPromise();
    const ev = new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [{ merchandiseId: String(VID), quantity: 0 }],
      promise: d.promise,
    });
    tuPhat = true;
    document.dispatchEvent(ev);
    tuPhat = false;
    d.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { source: 'gf-cn', sourceId: 'gf-cn', itemCount: cart.item_count, items: cart.items },
    });
  } catch (err) {
    tuPhat = false;
    console.warn('[gf-cn] theme refresh', err);
    if (/\/cart\/?$/.test(location.pathname)) location.reload();
  }
  document.dispatchEvent(new CustomEvent('gf-cn:cart-synced', { detail: { cart } }));
}

async function dongBo() {
  if (dang) {
    lai = true;
    return;
  }
  dang = true;
  try {
    const cart = await layGio();
    let can = 0;
    const phi = [];
    for (const it of cart.items || []) {
      if (it.variant_id === VID) {
        phi.push(it);
        continue;
      }
      const n = parseInt((it.properties && it.properties._gf_cn) || '0', 10);
      if (n > 0) can += n * it.quantity;
    }
    const dangCo = phi.reduce((s, it) => s + it.quantity, 0);
    if (dangCo === can && phi.length <= 1) return;
    let r;
    if (!phi.length) {
      r = await goi('cart/add.js', { items: [{ id: VID, quantity: can }] });
    } else {
      const updates = {};
      phi.forEach((it, i) => {
        updates[it.key] = i === 0 ? can : 0;
      });
      r = await goi('cart/update.js', { updates });
    }
    if (r.ok) baoTheme(await layGio());
    else console.warn('[gf-cn] cart sync HTTP', r.status);
  } catch (err) {
    console.warn('[gf-cn] cart sync', err);
  } finally {
    dang = false;
    if (lai) {
      lai = false;
      henDongBo();
    }
  }
}

window.GF_CN = { version: '1.9', sync: dongBo };
if (document.querySelector('script[data-gf-cn-lech]')) henDongBo(600);
